import { useState, useEffect, useCallback } from 'react';
import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  DollarOutlined,
  FundOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import {
  ContainerForm,
  AInput,
  AButton,
  AStatisticCard,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../../components';
import { IFinancial } from '../../../../shared/interfaces';
import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';
import { Col, Flex, Row, Table } from 'antd';
import { currency } from '../../../../shared/utils/functions';
import {
  formatDate,
  getInitialFinalDateMonth,
} from '../../../../shared/utils/format-date';
import { ContainerFilterMovement } from './styles';
import MovementsFinancialPrint from './Print';
import { useNavigate } from 'react-router-dom';

const MovementsFinancials: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [movements, setMovements] = useState<IFinancial[]>([]);
  const [totalPaid, setTotalPaid] = useState<number>(0);
  const [totalReceived, setTotalReceived] = useState<number>(0);
  const [modalPrint, setModalPrint] = useState<boolean>(false);
  const date = getInitialFinalDateMonth();
  const [loading, setLoading] = useState<boolean>(false);
  const [date_initial, setDate_initial] = useState<string | null>(date.dateI);
  const [date_final, setDate_final] = useState<string | null>(date.dateF);

  const list = useCallback(async () => {
    setLoading(true);
    setMovements([]);
    try {
      const response = await api.post('/financials/movement', {
        financial_plan: null,
        date_initial,
        date_final,
      });
      setMovements(response.data);
      const received = response.data
        .filter((x: IFinancial) => x.type === 1)
        .reduce((sum: number, item: IFinancial) => (sum += +item.value), 0);
      const paid = response.data
        .filter((x: IFinancial) => x.type === 0)
        .reduce((sum: number, item: IFinancial) => (sum += +item.value), 0);
      setTotalPaid(paid);
      setTotalReceived(received);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar contas',
      });
    } finally {
      setLoading(false);
    }
  }, [date_initial, date_final, addToast]);

  const handlePrint = useCallback(() => {
    setModalPrint(true);
  }, []);

  useEffect(() => {
    list();
  }, [list]);

  return (
    <PageLayout>
      <PageIntro
        badge="Relatórios"
        icon={<FundOutlined />}
        title="Movimentação financeira"
        description="Analise recebimentos, pagamentos e saldo consolidado em uma visão operacional padronizada."
        actions={
          <AButton
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/financials')}
          >
            Voltar
          </AButton>
        }
      />
      <PagePanel>
        <ContainerForm>
          <Row style={{ width: '100%', marginBottom: 5 }}>
            <ContainerFilterMovement>
              <AInput
                type="date"
                prefix={<CalendarOutlined />}
                value={date_initial || ''}
                onChange={(e) => setDate_initial(e.target.value)}
              />

              <AInput
                type="date"
                prefix={<CalendarOutlined />}
                value={date_final || ''}
                onChange={(e) => setDate_final(e.target.value)}
              />
              <AButton
                type="primary"
                icon={<PrinterOutlined />}
                onClick={handlePrint}
              >
                Imprimir
              </AButton>
            </ContainerFilterMovement>
          </Row>
          <Row gutter={16} style={{ marginBottom: 10 }}>
            <Col span={8}>
              <AStatisticCard
                title="Recebido"
                value={currency(totalReceived)}
                color="#3f8600"
                icon={<ArrowDownOutlined />}
              />
            </Col>
            <Col span={8}>
              <AStatisticCard
                title="Pago"
                value={currency(totalPaid)}
                color="#cf1322"
                icon={<ArrowUpOutlined />}
              />
            </Col>
            <Col span={8}>
              <AStatisticCard
                title="Saldo"
                value={currency(totalReceived - totalPaid)}
                color="#000"
                icon={<DollarOutlined />}
              />
            </Col>
          </Row>

          <Table
            className="virtual-table"
            scroll={{ y: 510 }}
            columns={[
              {
                title: 'Tipo',
                dataIndex: 'type',
                width: 65,
                render: (value: number) =>
                  value ? (
                    <ArrowDownOutlined
                      style={{ color: '#32cf5c', fontSize: 25 }}
                    />
                  ) : (
                    <ArrowUpOutlined
                      style={{ color: '#ed4140', fontSize: 25 }}
                    />
                  ),
              },
              {
                title: 'D. Pagamento',
                dataIndex: 'payment_date',
                width: 135,
                render: (value: string) => <>{formatDate(value)}</>,
              },
              {
                title: 'D. Vencimento',
                dataIndex: 'due_date',
                width: 135,
                render: (value: string) => <>{formatDate(value)}</>,
              },
              {
                title: 'Cliente',
                dataIndex: 'client_name',
              },
              {
                title: 'Descrição',
                dataIndex: 'description',
              },
              {
                title: 'Valor',
                dataIndex: 'value',
                width: 145,
                render: (value: string | number) => <>{currency(value)}</>,
              },
              {
                title: 'Forma de Pagamento',
                dataIndex: 'method_payment',
              },
            ]}
            pagination={false}
            loading={loading}
            dataSource={movements}
          />
          {modalPrint && (
            <MovementsFinancialPrint
              open={modalPrint}
              close={() => setModalPrint(false)}
              report={{ data: movements, totalPaid, totalReceived }}
            />
          )}
        </ContainerForm>
      </PagePanel>
    </PageLayout>
  );
};

export default MovementsFinancials;
